import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  Snackbar, 
  Alert,
  Paper
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../i18n';
import { notificationsApi } from '../../services/api';

const ContactForm = () => {
  const { language } = useSelector((state) => state.ui);
  const translations = getTranslation(language);
  const t = translations.contact.form;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });

  // Error state
  const [errors, setErrors] = useState({});

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = t.nameRequired;
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = t.emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.emailInvalid;
    }

    // Validate subject
    if (!formData.subject.trim()) {
      newErrors.subject = t.subjectRequired;
    }

    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = t.messageRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Send notification to Telegram
  const sendNotification = async (data) => {
    try {
      // Format message for Telegram notification
      const telegramMessage = `
${t.notificationTitle}
${t.notificationName} ${data.name}
${t.notificationEmail} ${data.email}
${data.company ? `${t.notificationCompany} ${data.company}` : ''}
${t.notificationSubject} ${data.subject}

${t.notificationMessage}
${data.message}
      `.trim();

      // Send notification to Telegram
      await notificationsApi.sendTelegramNotification('landing', telegramMessage);
      return true;
    } catch (error) {
      console.error('Error sending notification:', error);
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Show loading state
      setSnackbar({
        open: true,
        message: t.sending,
        severity: 'info'
      });

      // Send the notification
      const success = await sendNotification(formData);

      // Show success or error message
      setSnackbar({
        open: true,
        message: success ? t.success : t.error,
        severity: success ? 'success' : 'error'
      });

      // Reset form if successful
      if (success) {
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: ''
        });
      }
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {translations.contact.subtitle}
            </Typography>
          </Grid>

          {/* Name field */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              label={t.name}
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>

          {/* Email field */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="email"
              name="email"
              label={t.email}
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>

          {/* Company field */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="company"
              name="company"
              label={t.company}
              value={formData.company}
              onChange={handleChange}
            />
          </Grid>


          {/* Subject field */}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="subject"
              name="subject"
              label={t.subject}
              value={formData.subject}
              onChange={handleChange}
              error={!!errors.subject}
              helperText={errors.subject}
            />
          </Grid>

          {/* Message field */}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="message"
              name="message"
              label={t.message}
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange}
              error={!!errors.message}
              helperText={errors.message}
            />
          </Grid>

          {/* Submit button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 2 }}
            >
              {t.submit}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Success/Error message */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ContactForm;
